import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart3, Calendar, Clock, Users, Settings,
  LogOut, Menu, X, ChevronDown, Plus, Trash2, Save, Home,
  TrendingUp, CheckCircle, AlertCircle, Car, FileText, Mail, Phone,
  Edit2, Search, Download, Eye, UserPlus, Shield, User, Package, Warehouse, ClipboardCheck, Star, Receipt, BookOpen, CalendarOff, AlertTriangle, Gift, MailPlus, RotateCcw, Wallet, Wrench, Banknote
} from "lucide-react";
import JobCompletionModal from "../components/dashboard/JobCompletionModal";
import JobCardViewModal from "../components/dashboard/JobCardViewModal";
import JobCheckInModal from "../components/dashboard/JobCheckInModal";
import JobBoardSection from "../components/dashboard/JobBoardSection";
import InstallerScorecard from "../components/dashboard/InstallerScorecard";
import { AdjustServicesDialog } from "../components/dashboard/AdjustServicesDialog";
import { CorrectDepositDialog } from "../components/dashboard/CorrectDepositDialog";
import StockPullSection from "../components/dashboard/StockPullSection";
import StockInventorySection from "../components/dashboard/StockInventorySection";
import FinanceSection from "../components/dashboard/FinanceSection";
import PromotionsTab from "../components/dashboard/PromotionsTab";
import StockAnalytics from "../components/dashboard/StockAnalytics";
import JobProfitability from "../components/dashboard/JobProfitability";
import JobAlertsSection from "../components/dashboard/JobAlertsSection";
import StockTakeSection from "../components/dashboard/StockTakeSection";
import InvoicesTab from "../components/dashboard/InvoicesTab";
import BlogAdminTab from "../components/dashboard/BlogAdminTab";
import { CalendarReconcileButton } from "../components/dashboard/CalendarReconcileButton";
import { resolveVehicleModel } from "../utils/vehicleDisplay";
import BlockCalendarSection from "../components/dashboard/BlockCalendarSection";
import AuditLogCard from "../components/dashboard/AuditLogCard";
import EmailHealthPill from "../components/dashboard/EmailHealthPill";
import CollectionEmailAuditWidget from "../components/dashboard/CollectionEmailAuditWidget";
import ReviewFunnelWidget from "../components/dashboard/ReviewFunnelWidget";
import NewReviewsAlertWidget from "../components/dashboard/NewReviewsAlertWidget";
import NightlyReconcileTile from "../components/dashboard/NightlyReconcileTile";
import { BalanceDesyncAuditTile } from "../components/dashboard/BalanceDesyncAuditTile";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { API, useAuth } from "../App";
import {
  CashTakenPanel, PermissionsLegend, StaffPermissionToggles,
} from "../components/dashboard/StaffPermissions";
import { StockValuationCard } from "../components/dashboard/StockValuationCard";

// Sidebar Component
const Sidebar = ({ isOpen, setIsOpen, activeTab, setActiveTab, isAdmin }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // ---- PII-alert badge state -------------------------------------------------
  // Fires when any `data.pii_detected` audit event has landed in the last 7 days.
  // Emitted by the nightly retention scanner (services/retention.py). Read-only
  // — no backend changes for this indicator.
  const [piiAlertCount, setPiiAlertCount] = useState(0);
  const [jobAlertCount, setJobAlertCount] = useState(0);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const fetchJobAlerts = async () => {
      try {
        const [c, d] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/stock/cost-alerts?limit=1`),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/stock/duration-alerts?limit=1`),
        ]);
        if (!cancelled) {
          setJobAlertCount(
            (c.data?.unacknowledged_count || 0) + (d.data?.unacknowledged_count || 0)
          );
        }
      } catch (_e) {
        // Silent — badge is a nicety.
      }
    };
    fetchJobAlerts();
    const id = setInterval(fetchJobAlerts, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;

    const fetchPiiAlert = async () => {
      try {
        // NOTE: we can't use `severity_totals` from the response — the audit-log
        // endpoint aggregates severities over the whole time window regardless
        // of the `event_type` filter. `count` / `events.length` DO respect the
        // filter, so we use `limit=50` and count what actually comes back.
        // (Frontend-only workaround; backend endpoint unchanged in this pass.)
        const params = new URLSearchParams({
          event_type: "data.pii_detected",
          since_hours: "168",
          limit: "50",
        });
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/security/audit-log?${params}`
        );
        if (!cancelled) {
          const events = Array.isArray(res.data?.events) ? res.data.events : [];
          setPiiAlertCount(events.length);
        }
      } catch (_e) {
        // Silent — badge is a nicety, not critical UX.
      }
    };

    fetchPiiAlert();
    const id = setInterval(fetchPiiAlert, 5 * 60 * 1000); // refresh every 5min
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isAdmin]);

  const menuItems = [
    { id: "overview", icon: BarChart3, label: "Overview" },
    { id: "jobboard", icon: Car, label: "Job Board" },
    { id: "bookings", icon: Calendar, label: "Bookings" },
    ...(isAdmin ? [{ id: "blockcal", icon: CalendarOff, label: "Block Calendar" }] : []),
    ...(isAdmin ? [{ id: "quotes", icon: FileText, label: "Quote Requests" }] : []),
    ...(isAdmin ? [{ id: "promotions", icon: Gift, label: "Promotions" }] : []),
    { id: "stock", icon: Package, label: "Stock Pull" },
    ...(isAdmin ? [{ id: "inventory", icon: Warehouse, label: "Inventory" }] : []),
    { id: "stocktake", icon: ClipboardCheck, label: "Stock Take" },
    ...(isAdmin ? [{ id: "invoices", icon: Receipt, label: "Invoices" }] : []),
    ...(isAdmin ? [{ id: "finance", icon: Wallet, label: "Finance" }] : []),
    ...(isAdmin ? [{ id: "blog", icon: BookOpen, label: "Blog & Guides" }] : []),
    ...(isAdmin ? [{ id: "analytics", icon: TrendingUp, label: "Stock Analytics" }] : []),
    ...(isAdmin ? [{ id: "profitability", icon: TrendingUp, label: "Job Profitability" }] : []),
    ...(isAdmin ? [{ id: "job-alerts", icon: AlertTriangle, label: "Job Alerts", alertCount: jobAlertCount }] : []),
    ...(isAdmin ? [{ id: "staff", icon: Users, label: "Staff Management" }] : []),
    ...(isAdmin
      ? [{ id: "security", icon: Shield, label: "Security", alertCount: piiAlertCount }]
      : []),
    { id: "settings", icon: Settings, label: "Settings" },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      <aside
        className={`fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50 w-64 h-screen lg:h-screen bg-saturn-slate border-r border-slate-800 transform transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-slate-800 flex-shrink-0">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-saturn-orange flex items-center justify-center">
                <span className="font-orbitron font-bold text-white">SS</span>
              </div>
              <span className="font-orbitron font-bold text-white">SaturnStyling</span>
            </Link>
          </div>

          <nav className="flex-1 p-4 space-y-2 overflow-y-auto min-h-0" data-testid="sidebar-nav-scroll">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === item.id
                    ? "bg-saturn-orange text-white"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-rajdhani flex-1 text-left">{item.label}</span>
                {item.alertCount ? (
                  <span
                    className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500/90 text-slate-900 text-[10px] font-bold font-mono shadow-[0_0_0_2px_rgba(245,158,11,0.25)] animate-pulse"
                    title={`${item.alertCount} PII detection${item.alertCount === 1 ? "" : "s"} in the last 7 days`}
                    data-testid="sidebar-pii-badge"
                  >
                    {item.alertCount > 99 ? "99+" : item.alertCount}
                  </span>
                ) : null}
              </button>
            ))}
          </nav>

          <div className="p-4 border-t border-slate-800 flex-shrink-0" data-testid="sidebar-account-block">
            <div className="flex items-center gap-3 mb-4 px-2">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                  <User className="w-5 h-5 text-slate-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{user?.name || "User"}</p>
                <p className="text-xs text-slate-400 flex items-center gap-1">
                  {isAdmin ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
                  {isAdmin ? "Admin" : "Staff"}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
            {isAdmin && (
              <div className="mt-4 pt-4 border-t border-slate-800 flex justify-center" data-testid="dashboard-email-health-slot">
                <EmailHealthPill />
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

// Overview Tab
const OverviewTab = ({ bookings, setBookings, isAdmin }) => {
  const [completingBooking, setCompletingBooking] = useState(null);

  const today = new Date().toISOString().split("T")[0];
  // Redo visits are free return visits with no price — they are excluded
  // from every count on this tab, the same way they are excluded from
  // revenue, AOV and conversion on the server.
  const sold = bookings.filter((b) => !b.is_redo);
  const todayBookings = sold.filter(b => b.booking_date === today);
  const confirmedBookings = sold.filter(b => b.status === "confirmed");
  // Pending only counts bookings whose deposit hold is still LIVE — a
  // `pending_deposit` row past its `hold_expires_at` is an abandoned
  // Revolut checkout (customer never paid). The 5-min server sweeper
  // will flip these to `expired` shortly; filtering them out here means
  // the dashboard number is honest between sweeps, and stays honest if
  // a sweep is delayed for any reason (08 Feb 2026).
  const _nowIso = new Date().toISOString();
  const pendingBookings = sold.filter(
    (b) => b.status === "pending_deposit" && (!b.hold_expires_at || b.hold_expires_at > _nowIso),
  );

  const handleJobComplete = (result) => {
    if (result?.booking_status === "completed" && completingBooking) {
      setBookings(prev => prev.map(b => 
        b.booking_id === completingBooking.booking_id 
          ? { ...b, status: "completed" } 
          : b
      ));
    }
    setCompletingBooking(null);
  };

  return (
    <div className="space-y-6">
      <div className={`grid grid-cols-1 md:grid-cols-2 ${isAdmin ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-4`}>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Today&apos;s Bookings</p>
                <p className="text-3xl font-bold text-white mt-1">{todayBookings.length}</p>
              </div>
              <Calendar className="w-10 h-10 text-saturn-orange" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Confirmed</p>
                <p className="text-3xl font-bold text-green-400 mt-1">{confirmedBookings.length}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Pending</p>
                <p className="text-3xl font-bold text-yellow-400 mt-1">{pendingBookings.length}</p>
              </div>
              <AlertCircle className="w-10 h-10 text-yellow-400" />
            </div>
          </CardContent>
        </Card>

        {/* Total Revenue tile removed (Feb 2026) — figure was misleading:
            it summed `total_price` across every `confirmed` booking
            without filtering to actually-paid, without excluding cancelled
            or refunded bookings, and without applying any date scoping —
            so it drifted further from reality every day. Real revenue
            reporting lives in the VAT/Purchases + Job Profitability
            surfaces which draw from paid + completed states directly. */}
      </div>

      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Today&apos;s Schedule</CardTitle>
        </CardHeader>
        <CardContent>
          {todayBookings.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No bookings for today</p>
          ) : (
            <div className="space-y-3">
              {todayBookings.sort((a, b) => a.booking_time?.localeCompare(b.booking_time)).map((booking) => (
                <div key={booking.booking_id} className="flex items-center gap-4 p-4 rounded-lg bg-slate-900/50">
                  <div className="text-saturn-orange font-orbitron font-bold text-lg w-16">
                    {booking.booking_time}
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">{booking.customer_name}</p>
                    <p className="text-slate-400 text-sm">
                      {booking.vehicle?.year} {booking.vehicle?.make} {resolveVehicleModel(booking)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={
                      booking.status === "confirmed" ? "bg-green-500/20 text-green-400" : 
                      booking.status === "completed" ? "bg-blue-500/20 text-blue-400" :
                      "bg-yellow-500/20 text-yellow-400"
                    }>
                      {booking.status}
                    </Badge>
                    {isAdmin && booking.status === "confirmed" && (
                      <Button
                        size="sm"
                        className="bg-saturn-orange hover:bg-saturn-orange/80 h-7 text-xs"
                        onClick={() => setCompletingBooking(booking)}
                        data-testid={`overview-finish-${booking.booking_id}`}
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Open Job Card
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Job Completion Modal */}
      <JobCompletionModal
        isOpen={!!completingBooking}
        onClose={() => setCompletingBooking(null)}
        booking={completingBooking}
        onComplete={handleJobComplete}
      />

      {/* Nightly Reconcile Tile — admin-only self-healing status glance.
          Rendered here (after Today's Schedule) so the first Overview
          scroll surfaces any silent-drop failures. Tile hides itself
          on 401/403 for non-admin users. */}
      {isAdmin && <NightlyReconcileTile />}
      {isAdmin && <BalanceDesyncAuditTile />}
    </div>
  );
};

// Bookings Tab
const BookingsTab = ({ bookings, setBookings, isAdmin, services, permissions = {} }) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [editingBooking, setEditingBooking] = useState(null);
  const [adjustBooking, setAdjustBooking] = useState(null);
  const [correctDepositBooking, setCorrectDepositBooking] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // Job completion modal state
  const [completingBooking, setCompletingBooking] = useState(null);
  // Job card view modal state (for viewing completed jobs)
  const [viewingJobCard, setViewingJobCard] = useState(null);
  // Job check-in modal state
  const [checkingInBooking, setCheckingInBooking] = useState(null);
  // Job board refresh trigger
  const [jobBoardRefresh, setJobBoardRefresh] = useState(0);

  const filteredBookings = bookings.filter(b => {
    if (statusFilter !== "all" && b.status !== statusFilter) return false;
    if (search) {
      const searchLower = search.toLowerCase();
      // Reg is matched with separators stripped from BOTH sides, so
      // "12 D 1234", "12-d-1234" and "12d1234" all find the same car.
      const normalise = (v) => String(v || "").toLowerCase().replace(/[\s-]/g, "");
      const regNeedle = normalise(search);
      const regHay = normalise(b.vehicle_reg || b.vehicle?.reg);
      if (!b.customer_name?.toLowerCase().includes(searchLower) &&
          !b.customer_email?.toLowerCase().includes(searchLower) &&
          !b.customer_phone?.includes(search) &&
          !b.booking_id?.toLowerCase().includes(searchLower) &&
          !(regNeedle && regHay.includes(regNeedle))) {
        return false;
      }
    }
    if (dateFrom && b.booking_date < dateFrom) return false;
    if (dateTo && b.booking_date > dateTo) return false;
    return true;
  });

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      
      const response = await axios.get(`${API}/auth/bookings/export?${params}`, {
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `bookings_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Bookings exported successfully");
    } catch (error) {
      toast.error("Failed to export bookings");
    }
  };

  const handleDelete = async (bookingId) => {
    setDeleteConfirm(bookingId);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await axios.delete(`${API}/auth/bookings/${deleteConfirm}`);
      setBookings(prev => prev.filter(b => b.booking_id !== deleteConfirm));
      toast.success("Booking deleted successfully");
    } catch (error) {
      console.error("Delete error:", error.response?.data || error);
      toast.error(error.response?.data?.detail || "Failed to delete booking");
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleResendDepositLink = async (booking) => {
    try {
      const r = await axios.post(`${API}/auth/bookings/${booking.booking_id}/resend-deposit-link`);
      toast.success(
        r.data.reused_existing_order
          ? `Existing link resent · hold extended ${r.data.hold_minutes} min`
          : `New deposit link generated · hold reset to ${r.data.hold_minutes} min`
      );
      setBookings(prev => prev.map(b =>
        b.booking_id === booking.booking_id
          ? { ...b, hold_expires_at: r.data.hold_expires_at, payment_order_id: r.data.reused_existing_order ? b.payment_order_id : r.data.payment_order_id }
          : b
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend deposit link");
    }
  };

  // Resend the customer's "Booking Confirmed" email. Added Sep 2026 so
  // promo-code customers who got the pre-fix email (undiscounted
  // "Balance Due on Day") can be sent a corrected one.
  const handleResendConfirmation = async (booking) => {
    try {
      const r = await axios.post(`${API}/auth/bookings/${booking.booking_id}/resend-confirmation`);
      toast.success(
        r.data.discount_shown > 0
          ? `Confirmation resent to ${r.data.sent_to} · balance €${r.data.balance_shown.toFixed(2)} (€${r.data.discount_shown.toFixed(2)} discount applied)`
          : `Confirmation resent to ${r.data.sent_to} · balance €${r.data.balance_shown.toFixed(2)}`
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend confirmation");
    }
  };

  const handleReactivateBooking = async (booking) => {
    try {
      const r = await axios.post(`${API}/auth/bookings/${booking.booking_id}/reactivate`);
      toast.success(`Booking reactivated · fresh ${r.data.hold_minutes}-min hold · link emailed`);
      setBookings(prev => prev.map(b =>
        b.booking_id === booking.booking_id
          ? { ...b, status: "pending_deposit", hold_expires_at: r.data.hold_expires_at, expired_at: undefined, expired_reason: undefined }
          : b
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate booking");
    }
  };


  const handleStatusChange = async (bookingId, newStatus, booking) => {
    // Intercept "completed" status - open job completion modal instead
    if (newStatus === "completed" && booking?.status === "confirmed") {
      setCompletingBooking(booking);
      return;
    }
    
    try {
      await axios.put(`${API}/auth/bookings/${bookingId}`, { status: newStatus });
      setBookings(prev => prev.map(b => b.booking_id === bookingId ? { ...b, status: newStatus } : b));
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleJobComplete = (result) => {
    // Update the booking status in local state after job completion
    if (result?.booking_status === "completed" && completingBooking) {
      setBookings(prev => prev.map(b => 
        b.booking_id === completingBooking.booking_id 
          ? { ...b, status: "completed" } 
          : b
      ));
    }
    setCompletingBooking(null);
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-slate-400 mb-2 block">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, reg, email, phone, booking ID..."
                  className="pl-10 bg-slate-900 border-slate-700 text-white"
                  data-testid="bookings-search-input"
                />
              </div>
            </div>
            
            <div className="w-40">
              <Label className="text-slate-400 mb-2 block">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="pending_deposit">Pending</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="w-40">
              <Label className="text-slate-400 mb-2 block">From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>

            <div className="w-40">
              <Label className="text-slate-400 mb-2 block">To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>

            {/* ADMIN ONLY — the CSV carries customer email, phone and
                Total Price for every booking in range. */}
            {isAdmin && (
              <Button variant="outline" onClick={handleExport} className="border-slate-700 text-slate-300" data-testid="bookings-export-csv-btn">
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            )}

            {isAdmin && <CalendarReconcileButton />}

            {isAdmin && (
              <Button onClick={() => setShowAddModal(true)} className="bg-saturn-orange hover:bg-saturn-orange/90">
                <Plus className="w-4 h-4 mr-2" />
                Add Booking
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bookings Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700 hover:bg-transparent">
                <TableHead className="text-slate-400">ID</TableHead>
                <TableHead className="text-slate-400">Date & Time</TableHead>
                <TableHead className="text-slate-400">Customer</TableHead>
                <TableHead className="text-slate-400">Vehicle</TableHead>
                <TableHead className="text-slate-400">Services</TableHead>
                {isAdmin && <TableHead className="text-slate-400">Total</TableHead>}
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 8 : 7} className="text-center text-slate-400 py-8">
                    No bookings found
                  </TableCell>
                </TableRow>
              ) : (
                filteredBookings.map((booking) => (
                  <TableRow key={booking.booking_id} className="border-slate-700">
                    <TableCell className="text-white font-mono text-sm">{booking.booking_id}</TableCell>
                    <TableCell className="text-white">
                      <div>{booking.booking_date}</div>
                      <div className="text-saturn-orange">{booking.booking_time}</div>
                      {booking.collection_date && booking.collection_date !== booking.booking_date && (
                        <div className="text-green-400 text-xs mt-1">
                          Collection: {booking.collection_date}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-white">
                        {booking.customer_name}
                        {booking.is_redo && (
                          <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/25 text-amber-200 border border-amber-400/50"
                                title="Free return visit — no charge"
                                data-testid={`bookings-redo-badge-${booking.booking_id}`}>
                            REDO
                          </span>
                        )}
                      </div>
                      <div className="text-slate-400 text-sm">{booking.customer_email}</div>
                      <div className="text-slate-400 text-sm">{booking.customer_phone}</div>
                    </TableCell>
                    <TableCell className="text-slate-300 text-sm">
                      {booking.vehicle?.year} {booking.vehicle?.make} {resolveVehicleModel(booking)}
                    </TableCell>
                    <TableCell className="text-slate-300 text-sm max-w-[150px]">
                      {(() => {
                        // Prefer stored services_details names. Fallback:
                        // resolve service_ids via the loaded services list
                        // (so legacy rows missing services_details still show
                        // proper names instead of raw "svc_perf_full").
                        const names = (booking.services_details || [])
                          .map(s => s?.name)
                          .filter(Boolean);
                        if (names.length) return names.join(", ");
                        const byId = Object.fromEntries((services || []).map(s => [s.service_id, s.name]));
                        const resolved = (booking.service_ids || [])
                          .map(id => byId[id] || id);
                        return resolved.join(", ") || "—";
                      })()}
                    </TableCell>
                    {isAdmin && <TableCell className="text-saturn-orange font-bold">€{booking.total_price}</TableCell>}
                    <TableCell>
                      {isAdmin ? (
                        <Select value={booking.status} onValueChange={(v) => handleStatusChange(booking.booking_id, v, booking)}>
                          <SelectTrigger className="w-32 h-8 bg-transparent border-slate-700 text-sm" data-testid={`status-select-${booking.booking_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending_deposit">Pending</SelectItem>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge className={
                          booking.status === "confirmed" ? "bg-green-500/20 text-green-400" :
                          booking.status === "completed" ? "bg-blue-500/20 text-blue-400" :
                          booking.status === "cancelled" ? "bg-red-500/20 text-red-400" :
                          "bg-yellow-500/20 text-yellow-400"
                        }>
                          {booking.status}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {isAdmin && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-slate-400 hover:text-white"
                              onClick={() => setEditingBooking(booking)}
                              data-testid={`edit-booking-${booking.booking_id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {/* Scope/price change — the ONLY safe way to
                                move total_price, because it re-syncs the
                                balance, job log and invoice lines too. */}
                            {["confirmed", "checked_in", "in_progress"].includes(booking.status)
                              && !booking.balance_paid && !booking.invoice_id && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-saturn-orange hover:text-orange-300"
                                onClick={() => setAdjustBooking(booking)}
                                title="Change services / price"
                                data-testid={`adjust-services-${booking.booking_id}`}
                              >
                                <Wrench className="w-4 h-4" />
                              </Button>
                            )}
                            {/* The customer paid something other than the
                                standard €50 — usually a Revolut link sent
                                by hand on a big job. Lives HERE as well as
                                on the job card because the money lands
                                BEFORE the job, and the job card only opens
                                once the job is completed. */}
                            {!["completed", "cancelled", "expired"].includes(booking.status)
                              && !booking.balance_paid && !booking.invoice_id && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-emerald-400 hover:text-emerald-300"
                                onClick={() => setCorrectDepositBooking(booking)}
                                title="Correct deposit received"
                                data-testid={`correct-deposit-${booking.booking_id}`}
                              >
                                <Banknote className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-400 hover:text-red-300"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDelete(booking.booking_id);
                              }}
                              data-testid={`delete-booking-${booking.booking_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {/* Open Job Card button for confirmed bookings (admin only - staff use Job Board) */}
                        {isAdmin && booking.status === "pending_deposit" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-saturn-orange hover:text-saturn-orange/80"
                            onClick={() => handleResendDepositLink(booking)}
                            data-testid={`resend-deposit-link-${booking.booking_id}`}
                            title="Reuses existing Revolut order if still valid, else mints a fresh one. Extends hold."
                          >
                            <MailPlus className="w-4 h-4 mr-1" />
                            Resend Deposit Link
                          </Button>
                        )}
                        {isAdmin && booking.status !== "pending_deposit" && booking.customer_email && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-sky-400 hover:text-sky-300"
                            onClick={() => handleResendConfirmation(booking)}
                            data-testid={`resend-confirmation-${booking.booking_id}`}
                            title="Re-sends the Booking Confirmed email with the correct discounted balance"
                          >
                            <MailPlus className="w-4 h-4 mr-1" />
                            Resend Confirmation
                          </Button>
                        )}
                        {isAdmin && booking.status === "expired" && booking.expired_reason === "hold_window_elapsed" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-amber-400 hover:text-amber-300"
                            onClick={() => handleReactivateBooking(booking)}
                            data-testid={`reactivate-booking-${booking.booking_id}`}
                            title="Only shown for bookings auto-expired by the hold sweeper. Blocks if the slot has since been taken."
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Reactivate & Resend
                          </Button>
                        )}
                        {/* Open Job Card button for confirmed bookings (admin only - staff use Job Board) */}
                        {isAdmin && booking.status === "confirmed" && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-blue-400 hover:text-blue-300"
                              onClick={() => setCheckingInBooking(booking)}
                              data-testid={`check-in-${booking.booking_id}`}
                            >
                              <ClipboardCheck className="w-4 h-4 mr-1" />
                              Check In
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-green-400 hover:text-green-300"
                              onClick={() => setCompletingBooking(booking)}
                              data-testid={`open-job-card-${booking.booking_id}`}
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              Complete Job
                            </Button>
                          </>
                        )}
                        {/* Continue Job for checked-in bookings (admin only - staff use Job Board) */}
                        {isAdmin && (booking.status === "checked_in" || booking.status === "in_progress") && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-green-400 hover:text-green-300"
                            onClick={() => setCompletingBooking(booking)}
                            data-testid={`complete-job-${booking.booking_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Complete Job
                          </Button>
                        )}
                        {/* View Job Card button for completed bookings */}
                        {booking.status === "completed" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-saturn-orange hover:text-saturn-orange/80"
                            onClick={() => setViewingJobCard(booking)}
                            data-testid={`view-job-card-${booking.booking_id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View Job Card
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400">Delete Booking</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-300">Are you sure you want to delete booking <span className="text-white font-mono">{deleteConfirm}</span>?</p>
            <p className="text-slate-400 text-sm mt-2">This action cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="border-slate-700">
              Cancel
            </Button>
            <Button onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      {editingBooking && (
        <EditBookingModal
          booking={editingBooking}
          onClose={() => setEditingBooking(null)}
          onSave={(updated) => {
            setBookings(prev => prev.map(b => b.booking_id === updated.booking_id ? updated : b));
            setEditingBooking(null);
          }}
        />
      )}

      {/* Change services / price — re-syncs balance, job log and invoice */}
      <AdjustServicesDialog
        booking={adjustBooking}
        open={!!adjustBooking}
        onClose={() => setAdjustBooking(null)}
        onSaved={(res) => {
          // Patch the row in place, same as EditBookingModal does —
          // this tab holds `bookings` in state, there's no refetch here.
          setBookings((prev) => prev.map((b) => (
            b.booking_id === adjustBooking?.booking_id
              ? {
                ...b,
                total_price: res?.new?.total_price ?? b.total_price,
                balance_amount: res?.new?.balance_due ?? b.balance_amount,
                remaining_balance_due: res?.new?.balance_due ?? b.remaining_balance_due,
                duration_minutes: res?.new?.duration_minutes ?? b.duration_minutes,
                services_details: res?.new?.services ?? b.services_details,
              }
              : b)));
          setAdjustBooking(null);
        }}
      />

      {/* Correct deposit received — REPLACES the standard €50 with what the
          customer actually paid, and re-syncs the balance. */}
      <CorrectDepositDialog
        booking={correctDepositBooking}
        open={!!correctDepositBooking}
        onClose={() => setCorrectDepositBooking(null)}
        onSaved={(res) => {
          setBookings((prev) => prev.map((b) => (
            b.booking_id === correctDepositBooking?.booking_id
              ? {
                ...b,
                deposit_amount: res.deposit_amount,
                deposit_paid: true,
                payment_source_deposit: res.method,
                balance_amount: res.balance_due,
                remaining_balance_due: res.balance_due,
                deposit_corrections: [...(b.deposit_corrections || []), res.correction],
              }
              : b)));
          setCorrectDepositBooking(null);
        }}
      />

      {/* Add Modal */}
      {showAddModal && (
        <AddBookingModal
          services={services}
          onClose={() => setShowAddModal(false)}
          onSave={(newBooking) => {
            setBookings(prev => [newBooking, ...prev]);
            setShowAddModal(false);
          }}
        />
      )}

      {/* Job Completion Modal */}
      <JobCompletionModal
        isOpen={!!completingBooking}
        onClose={() => setCompletingBooking(null)}
        booking={completingBooking}
        onComplete={handleJobComplete}
      />

      {/* Job Card View Modal (for viewing completed jobs) */}
      <JobCardViewModal
        isAdmin={isAdmin}
        permissions={permissions}
        isOpen={!!viewingJobCard}
        onClose={() => setViewingJobCard(null)}
        booking={viewingJobCard}
      />

      {/* Job Check-In Modal */}
      <JobCheckInModal
        isAdmin={isAdmin}
        isOpen={!!checkingInBooking}
        onClose={() => setCheckingInBooking(null)}
        booking={checkingInBooking}
        onCheckInComplete={(data) => {
          // Update booking status in list
          setBookings(prev => prev.map(b => 
            b.booking_id === checkingInBooking?.booking_id 
              ? { ...b, status: "checked_in", vehicle_reg: data.vehicle_reg }
              : b
          ));
          setCheckingInBooking(null);
          setJobBoardRefresh(prev => prev + 1); // Trigger job board refresh
          toast.success("Vehicle checked in successfully!");
        }}
      />
    </div>
  );
};

// Edit Booking Modal
const EditBookingModal = ({ booking, onClose, onSave }) => {
  const [form, setForm] = useState({
    booking_date: booking.booking_date || "",
    booking_time: booking.booking_time || "",
    customer_name: booking.customer_name || "",
    customer_email: booking.customer_email || "",
    customer_phone: booking.customer_phone || "",
    duration_minutes: booking.duration_minutes || booking.total_duration || 60,
    notes: booking.notes || "",
  });
  const [loading, setLoading] = useState(false);
  const [otherBookings, setOtherBookings] = useState([]); // occupied slots on the selected date

  // Whenever the admin picks a new date, fetch OTHER bookings for that
  // date/location so we can flag conflicts before submit. Excludes the
  // booking being edited itself.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!form.booking_date) { setOtherBookings([]); return; }
      try {
        const r = await axios.get(`${API}/bookings/day-schedule`, {
          params: { location_id: booking.location_id || "loc_saturn_duleek", date: form.booking_date },
        });
        if (cancelled) return;
        const rows = (r.data?.bookings || r.data || [])
          .filter(b => b.booking_id !== booking.booking_id && !["cancelled", "expired"].includes(b.status));
        setOtherBookings(rows);
      } catch (_e) {
        // Endpoint may not exist yet — fail silently. Backend still enforces
        // the conflict server-side (returns 409 on clash).
        setOtherBookings([]);
      }
    })();
    return () => { cancelled = true; };
  }, [form.booking_date, booking.booking_id, booking.location_id]);

  const conflictingBooking = otherBookings.find(b => b.booking_time === form.booking_time);

  const handleSubmit = async () => {
    if (conflictingBooking) {
      toast.error(`That slot is taken by ${conflictingBooking.customer_name || conflictingBooking.booking_id}`);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.put(`${API}/auth/bookings/${booking.booking_id}`, form);
      onSave(response.data);
      toast.success("Booking updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update booking");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Booking: {booking.booking_id}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-400">Date</Label>
              <Input
                type="date"
                value={form.booking_date}
                onChange={(e) => setForm({ ...form, booking_date: e.target.value })}
                className="bg-slate-800 border-slate-700"
                data-testid="edit-booking-date"
              />
            </div>
            <div>
              <Label className="text-slate-400">Time</Label>
              <Input
                type="time"
                value={form.booking_time}
                onChange={(e) => setForm({ ...form, booking_time: e.target.value })}
                className={`bg-slate-800 ${conflictingBooking ? "border-red-500 focus:ring-red-500" : "border-slate-700"}`}
                data-testid="edit-booking-time"
              />
            </div>
          </div>
          {form.booking_date && (
            <div
              className={`text-xs rounded-lg p-2 border ${conflictingBooking ? "bg-red-950/30 border-red-500/40 text-red-200" : "bg-slate-800/60 border-slate-700 text-slate-300"}`}
              data-testid="edit-booking-conflict-hint"
            >
              {conflictingBooking ? (
                <span>
                  ⚠️ Slot {form.booking_time} is already taken by{" "}
                  <span className="font-semibold">
                    {conflictingBooking.customer_name || conflictingBooking.booking_id}
                  </span>
                  . Pick a different time.
                </span>
              ) : otherBookings.length === 0 ? (
                <span>No other bookings on {form.booking_date} — slot is free.</span>
              ) : (
                <span>
                  {otherBookings.length} other booking{otherBookings.length === 1 ? "" : "s"} on this date:{" "}
                  {otherBookings.slice(0, 6).map(b => b.booking_time).join(", ")}
                  {otherBookings.length > 6 ? ` +${otherBookings.length - 6} more` : ""}
                </span>
              )}
            </div>
          )}
          <div>
            <Label className="text-slate-400">Customer Name</Label>
            <Input
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              className="bg-slate-800 border-slate-700"
            />
          </div>
          <div>
            <Label className="text-slate-400">Email</Label>
            <Input
              type="email"
              value={form.customer_email}
              onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
              className="bg-slate-800 border-slate-700"
            />
          </div>
          <div>
            <Label className="text-slate-400">Phone</Label>
            <Input
              value={form.customer_phone}
              onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
              className="bg-slate-800 border-slate-700"
            />
          </div>
          <div>
            <Label className="text-slate-400">Duration (minutes)</Label>
            <Input
              type="number"
              value={form.duration_minutes}
              onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) })}
              className="bg-slate-800 border-slate-700"
            />
          </div>
          <div>
            <Label className="text-slate-400">Notes</Label>
            <Input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="bg-slate-800 border-slate-700"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-700">Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-saturn-orange">
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Add Booking Modal
const AddBookingModal = ({ services, onClose, onSave }) => {
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_year: new Date().getFullYear().toString(),
    vehicle_size: "sedan",
    vehicle_reg: "",
    service_ids: [],
    booking_date: "",
    booking_time: "10:00",
    custom_price: "",
    use_custom_price: false,
    notes: "",
    status: "confirmed",
    send_emails: true,
    deposit_paid_cash: false,
    balance_paid_cash: false,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!form.customer_name || !form.customer_email || !form.booking_date || form.service_ids.length === 0) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        ...form,
        vehicle_reg: form.vehicle_reg.trim().toUpperCase() || null,
        custom_price: form.use_custom_price && form.custom_price ? parseFloat(form.custom_price) : null,
      };
      const response = await axios.post(`${API}/auth/bookings/manual`, payload);
      onSave(response.data);
      const msg = form.balance_paid_cash
        ? "Booking created + marked fully paid (cash) — invoice queued"
        : form.deposit_paid_cash
        ? "Booking created + deposit marked paid (cash)"
        : "Booking created — customer confirmation email sent";
      toast.success(msg);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setLoading(false);
    }
  };

  // Group services by category for better organization
  const servicesByCategory = {
    tinting: services.filter(s => s.category?.includes("tinting")),
    wrapping: services.filter(s => s.category === "wrapping"),
    polishing: services.filter(s => s.category === "polishing"),
    other: services.filter(s => ["coating", "misc"].includes(s.category))
  };

  const categoryLabels = {
    tinting: "Window Tinting",
    wrapping: "Vehicle Wrapping",
    polishing: "Paint Correction & Polishing",
    other: "Other Services"
  };

  // Generate year options (current year + 1 to 1990)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 1989 + 1 }, (_, i) => currentYear + 1 - i);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-saturn-orange" />
            Add Manual Booking
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Customer Info */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-saturn-orange uppercase tracking-wide">Customer Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400">Customer Name *</Label>
                <Input
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <Label className="text-slate-400">Email *</Label>
                <Input
                  type="email"
                  value={form.customer_email}
                  onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  placeholder="john@example.com"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-400">Phone</Label>
              <Input
                value={form.customer_phone}
                onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                className="bg-slate-800 border-slate-700"
                placeholder="+353 85 123 4567"
              />
            </div>
          </div>

          {/* Vehicle Info */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-saturn-orange uppercase tracking-wide">Vehicle Information</h4>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-slate-400">Make</Label>
                <Input
                  value={form.vehicle_make}
                  onChange={(e) => setForm({ ...form, vehicle_make: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  placeholder="BMW"
                />
              </div>
              <div>
                <Label className="text-slate-400">Model</Label>
                <Input
                  value={form.vehicle_model}
                  onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  placeholder="3 Series"
                />
              </div>
              <div>
                <Label className="text-slate-400">Year</Label>
                <Select value={form.vehicle_year} onValueChange={(v) => setForm({ ...form, vehicle_year: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-48">
                    {yearOptions.map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-400">Size</Label>
                <Select value={form.vehicle_size} onValueChange={(v) => setForm({ ...form, vehicle_size: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Small (Hatch)</SelectItem>
                    <SelectItem value="sedan">Medium (Sedan)</SelectItem>
                    <SelectItem value="estate">Large (Estate)</SelectItem>
                    <SelectItem value="suv">SUV</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-slate-400">Vehicle Registration</Label>
              <Input
                value={form.vehicle_reg}
                onChange={(e) => setForm({ ...form, vehicle_reg: e.target.value.toUpperCase() })}
                className="bg-slate-800 border-slate-700 uppercase"
                placeholder="231-D-12345"
              />
              <p className="text-xs text-slate-500 mt-1">For warranty tracking</p>
            </div>
          </div>

          {/* Booking Date/Time */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-saturn-orange uppercase tracking-wide">Booking Schedule</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-400">Date *</Label>
                <Input
                  type="date"
                  value={form.booking_date}
                  onChange={(e) => setForm({ ...form, booking_date: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div>
                <Label className="text-slate-400">Time</Label>
                <Input
                  type="time"
                  value={form.booking_time}
                  onChange={(e) => setForm({ ...form, booking_time: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div>
                <Label className="text-slate-400">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="pending_deposit">Pending Deposit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-saturn-orange uppercase tracking-wide">Services *</h4>
            <div className="max-h-48 overflow-y-auto p-2 bg-slate-800 rounded-lg space-y-3">
              {(() => {
                // Mirror backend size_map (routes/auth.py::create_manual_booking).
                // Keep in sync — Size dropdown drives pricing verbatim.
                const SIZE_MAP = { small: "hatch", medium: "saloon", sedan: "saloon", large: "estate", estate: "estate", suv: "suv", hatch: "hatch", saloon: "saloon" };
                const _tier = SIZE_MAP[form.vehicle_size] || "saloon";
                return Object.entries(servicesByCategory).map(([category, categoryServices]) => (
                categoryServices.length > 0 && (
                  <div key={category}>
                    <p className="text-xs text-slate-400 font-semibold mb-1">{categoryLabels[category]}</p>
                    <div className="grid grid-cols-2 gap-1">
                      {categoryServices.map((service) => {
                        const sizedPrice = service.price_by_size?.[_tier] ?? service.base_price ?? 0;
                        return (
                        <label key={service.service_id} className={`flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-700 p-2 rounded ${form.service_ids.includes(service.service_id) ? 'bg-saturn-orange/20 border border-saturn-orange/50' : ''}`}>
                          <input
                            type="checkbox"
                            checked={form.service_ids.includes(service.service_id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setForm({ ...form, service_ids: [...form.service_ids, service.service_id] });
                              } else {
                                setForm({ ...form, service_ids: form.service_ids.filter(id => id !== service.service_id) });
                              }
                            }}
                            className="rounded accent-saturn-orange"
                          />
                          <span className="truncate flex-1">{service.name}</span>
                          {sizedPrice > 0 && (
                            <span
                              className="text-xs text-slate-400"
                              data-testid={`manual-svc-price-${service.service_id}`}
                            >
                              €{sizedPrice}
                            </span>
                          )}
                        </label>
                        );
                      })}
                    </div>
                  </div>
                )
                ));
              })()}
            </div>
            {/* Running total — mirrors backend total_price incl. Sunday premium */}
            {(() => {
              const SIZE_MAP = { small: "hatch", medium: "saloon", sedan: "saloon", large: "estate", estate: "estate", suv: "suv", hatch: "hatch", saloon: "saloon" };
              const _tier = SIZE_MAP[form.vehicle_size] || "saloon";
              const _selected = (services || []).filter(s => form.service_ids.includes(s.service_id));
              const _subtotal = _selected.reduce((n, s) => n + (Number(s.price_by_size?.[_tier] ?? s.base_price ?? 0) || 0), 0);
              const _isSunday = form.booking_date && new Date(form.booking_date + "T00:00:00").getDay() === 0;
              const _premium = _isSunday ? 50 : 0;
              const _total = _subtotal + _premium;
              return (
                <div
                  className="flex items-center justify-between bg-slate-800/60 border border-slate-700 rounded-lg px-4 py-2 mt-2"
                  data-testid="manual-booking-total"
                >
                  <div className="text-slate-400 text-sm">
                    Total ({_selected.length} service{_selected.length === 1 ? "" : "s"}
                    {_premium ? " + Sunday premium €50" : ""} · size: {_tier})
                  </div>
                  <div className="text-saturn-orange font-bold text-lg" data-testid="manual-booking-total-amount">
                    €{_total.toFixed(2)}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Custom Price */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="use_custom_price"
                checked={form.use_custom_price}
                onChange={(e) => setForm({ ...form, use_custom_price: e.target.checked })}
                className="rounded accent-saturn-orange"
              />
              <Label htmlFor="use_custom_price" className="text-slate-400 cursor-pointer">Use Custom Price</Label>
            </div>
            {form.use_custom_price && (
              <div>
                <Label className="text-slate-400">Custom Price (€)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.custom_price}
                  onChange={(e) => setForm({ ...form, custom_price: e.target.value })}
                  className="bg-slate-800 border-slate-700 w-32"
                  placeholder="0.00"
                />
                <p className="text-xs text-slate-500 mt-1">Override calculated price</p>
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <Label className="text-slate-400">Notes</Label>
            <Input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="bg-slate-800 border-slate-700"
              placeholder="Any special instructions..."
            />
          </div>

          {/* Payment + email options */}
          <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-3 space-y-2">
            <p className="text-xs uppercase tracking-wider text-orange-400 font-semibold">Payment &amp; Notifications</p>
            <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer" data-testid="manual-booking-send-emails">
              <input
                type="checkbox"
                checked={form.send_emails}
                onChange={(e) => setForm({ ...form, send_emails: e.target.checked })}
                className="rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500"
              />
              Send customer confirmation email + admin new-booking alert
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer" data-testid="manual-booking-deposit-cash">
              <input
                type="checkbox"
                checked={form.deposit_paid_cash}
                onChange={(e) => setForm({ ...form, deposit_paid_cash: e.target.checked, balance_paid_cash: e.target.checked ? form.balance_paid_cash : false })}
                className="rounded border-slate-600 bg-slate-800 text-orange-500"
              />
              Mark deposit paid in cash (€50 or job total, whichever is lower)
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer" data-testid="manual-booking-balance-cash">
              <input
                type="checkbox"
                checked={form.balance_paid_cash}
                onChange={(e) => setForm({ ...form, balance_paid_cash: e.target.checked, deposit_paid_cash: e.target.checked ? true : form.deposit_paid_cash })}
                className="rounded border-slate-600 bg-slate-800 text-orange-500"
              />
              Fully paid in cash (deposit + balance) — auto-generate + email VAT invoice
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-700">Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-saturn-orange hover:bg-saturn-orange/80">
            {loading ? "Creating..." : "Create Booking"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Staff Management Tab (Admin Only)
const StaffTab = () => {  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);

  const [permRegistry, setPermRegistry] = useState([]);

  useEffect(() => {
    fetchStaff();
    // Toggles are rendered from the backend registry so a new flag
    // needs no frontend change.
    axios.get(`${API}/auth/staff/permissions/registry`)
      .then((r) => setPermRegistry(r.data.permissions || []))
      .catch(() => setPermRegistry([]));
  }, []);

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/auth/staff`);
      setStaff(response.data);
    } catch (error) {
      toast.error("Failed to load staff");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (staffId) => {
    if (!window.confirm("Are you sure you want to delete this staff account?")) return;
    
    try {
      await axios.delete(`${API}/auth/staff/${staffId}`);
      setStaff(prev => prev.filter(s => s.user_id !== staffId));
      toast.success("Staff deleted");
    } catch (error) {
      toast.error("Failed to delete staff");
    }
  };

  const handleToggleActive = async (staffId, isActive) => {
    try {
      await axios.put(`${API}/auth/staff/${staffId}`, { is_active: !isActive });
      setStaff(prev => prev.map(s => s.user_id === staffId ? { ...s, is_active: !isActive } : s));
      toast.success(`Staff ${!isActive ? "activated" : "deactivated"}`);
    } catch (error) {
      toast.error("Failed to update staff");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-white">Staff Accounts</h2>
        <Button onClick={() => setShowAddModal(true)} className="bg-saturn-orange hover:bg-saturn-orange/90">
          <UserPlus className="w-4 h-4 mr-2" />
          Add Staff
        </Button>
      </div>

      {/* Quality scorecard — admin only, and the endpoint refuses a staff
          token, so no permission switch can ever unlock it. */}
      <InstallerScorecard />

      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700 hover:bg-transparent">
                <TableHead className="text-slate-400">Name</TableHead>
                <TableHead className="text-slate-400">Email</TableHead>
                <TableHead className="text-slate-400">Created</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400 min-w-[320px]">Permissions</TableHead>
                <TableHead className="text-slate-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-slate-400 py-8">Loading...</TableCell>
                </TableRow>
              ) : staff.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-slate-400 py-8">No staff accounts yet</TableCell>
                </TableRow>
              ) : (
                staff.map((s) => (
                  <TableRow key={s.user_id} className="border-slate-700">
                    <TableCell className="text-white">{s.name}</TableCell>
                    <TableCell className="text-slate-300">{s.email}</TableCell>
                    <TableCell className="text-slate-400 text-sm">
                      {s.created_at ? new Date(s.created_at).toLocaleDateString() : "N/A"}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={s.is_active !== false}
                        onCheckedChange={() => handleToggleActive(s.user_id, s.is_active !== false)}
                      />
                    </TableCell>
                    <TableCell>
                      <StaffPermissionToggles
                        member={s}
                        registry={permRegistry}
                        onChanged={(userId, permissions) =>
                          setStaff(prev => prev.map(x =>
                            x.user_id === userId ? { ...x, permissions } : x))}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-slate-400 hover:text-white"
                          onClick={() => setEditingStaff(s)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => handleDelete(s.user_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <PermissionsLegend />

      <CashTakenPanel />

      {showAddModal && (
        <AddStaffModal
          onClose={() => setShowAddModal(false)}
          onSave={(newStaff) => {
            setStaff(prev => [...prev, newStaff]);
            setShowAddModal(false);
          }}
        />
      )}

      {editingStaff && (
        <EditStaffModal
          staff={editingStaff}
          onClose={() => setEditingStaff(null)}
          onSave={(updated) => {
            setStaff(prev => prev.map(s => s.user_id === updated.user_id ? updated : s));
            setEditingStaff(null);
          }}
        />
      )}
    </div>
  );
};

// Shared pay-config sub-form for Add/Edit Staff modals.
// Renders a pay_model dropdown + a pay_rate input whose label/suffix
// swap based on the selected model. Both optional — leaving them blank
// means "no labour cost gets attributed to this staff's jobs yet",
// which is a valid state (matches backend behaviour where labour_cost
// is null, NOT zero, when config is missing).
const PayConfigFields = ({ payModel, payRate, onChange }) => {
  const isHourly = payModel === "hourly";
  const isPct = payModel === "pct_gross" || payModel === "pct_net";
  const rateSuffix = isHourly ? "€/hr" : isPct ? "%" : "";
  const rateLabel = isHourly
    ? "Hourly rate (€/hr)"
    : payModel === "pct_gross"
    ? "% of gross price"
    : payModel === "pct_net"
    ? "% of net price (excl. VAT + materials)"
    : "Pay rate";
  return (
    <div className="border-t border-slate-800 pt-4 space-y-3" data-testid="pay-config-fields">
      <div>
        <Label className="text-slate-400">Pay model</Label>
        <p className="text-xs text-slate-500 mb-1.5">
          Used to calculate this staff&apos;s labour cost per completed job. Optional — leave blank to skip.
        </p>
        <select
          value={payModel || ""}
          onChange={(e) => {
            const next = e.target.value;
            // Switching to blank clears the rate too — one-sided config is invalid.
            onChange({ pay_model: next, ...(next === "" ? { pay_rate: "" } : {}) });
          }}
          className="w-full bg-slate-800 border border-slate-700 rounded-md h-10 px-3 text-white"
          data-testid="pay-model-select"
        >
          <option value="">— No pay model set —</option>
          <option value="hourly">Hourly (€/hr × real duration)</option>
          <option value="pct_gross">% of Gross price</option>
          <option value="pct_net">% of Net price (excl. VAT + materials)</option>
        </select>
      </div>
      <div>
        <Label className="text-slate-400">{rateLabel}</Label>
        <div className="relative">
          <Input
            type="number"
            min="0"
            max={isPct ? 100 : undefined}
            step={isHourly ? "0.5" : "0.1"}
            inputMode="decimal"
            value={payRate ?? ""}
            onChange={(e) => onChange({ pay_rate: e.target.value })}
            disabled={!payModel}
            className="bg-slate-800 border-slate-700 pr-14"
            placeholder={payModel ? (isHourly ? "e.g. 25" : "e.g. 30") : "Select a pay model first"}
            data-testid="pay-rate-input"
          />
          {rateSuffix && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">
              {rateSuffix}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Add Staff Modal
const AddStaffModal = ({ onClose, onSave }) => {
  const [form, setForm] = useState({ name: "", email: "", password: "", pay_model: "", pay_rate: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error("Please fill in all fields");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    // Pay config is optional. If EITHER field is set, BOTH must be —
    // matches the backend both-or-neither validator so we surface the
    // error before the round-trip.
    const modelSet = !!form.pay_model;
    const rateStr = String(form.pay_rate || "").trim();
    const rateSet = rateStr !== "";
    if (modelSet !== rateSet) {
      toast.error("Set pay model AND rate together — or leave both blank");
      return;
    }
    let payload = { name: form.name, email: form.email, password: form.password };
    if (modelSet && rateSet) {
      const rate = Number(rateStr);
      if (!Number.isFinite(rate) || rate < 0) {
        toast.error("Pay rate must be a non-negative number");
        return;
      }
      if ((form.pay_model === "pct_gross" || form.pay_model === "pct_net") && rate > 100) {
        toast.error("Percentage pay rate must be between 0 and 100");
        return;
      }
      payload = { ...payload, pay_model: form.pay_model, pay_rate: rate };
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/staff`, payload);
      onSave(response.data);
      toast.success("Staff account created");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create staff");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle>Add Staff Account</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label className="text-slate-400">Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-slate-800 border-slate-700"
              placeholder="Staff name"
            />
          </div>
          <div>
            <Label className="text-slate-400">Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-slate-800 border-slate-700"
              placeholder="staff@example.com"
            />
          </div>
          <div>
            <Label className="text-slate-400">Password</Label>
            <Input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="bg-slate-800 border-slate-700"
              placeholder="Min 6 characters"
            />
          </div>
          <PayConfigFields
            payModel={form.pay_model}
            payRate={form.pay_rate}
            onChange={(patch) => setForm({ ...form, ...patch })}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-700">Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-saturn-orange">
            {loading ? "Creating..." : "Create Staff"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Edit Staff Modal
const EditStaffModal = ({ staff, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: staff.name,
    password: "",
    // Pay config is admin-visible on staff docs coming out of GET /staff.
    // Prefill so a save-without-changes doesn't wipe existing values.
    pay_model: staff.pay_model || "",
    pay_rate: staff.pay_rate === null || staff.pay_rate === undefined ? "" : String(staff.pay_rate),
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    const update = { name: form.name };
    if (form.password) {
      if (form.password.length < 6) {
        toast.error("Password must be at least 6 characters");
        return;
      }
      update.password = form.password;
    }

    // Pay config — only send fields that changed. `""` means "clear it";
    // the backend accepts null for both, which restores the "no labour
    // cost attributed" state.
    const modelSet = !!form.pay_model;
    const rateStr = String(form.pay_rate || "").trim();
    const rateSet = rateStr !== "";
    if (modelSet !== rateSet) {
      toast.error("Set pay model AND rate together — or clear both");
      return;
    }
    if (modelSet && rateSet) {
      const rate = Number(rateStr);
      if (!Number.isFinite(rate) || rate < 0) {
        toast.error("Pay rate must be a non-negative number");
        return;
      }
      if ((form.pay_model === "pct_gross" || form.pay_model === "pct_net") && rate > 100) {
        toast.error("Percentage pay rate must be between 0 and 100");
        return;
      }
      update.pay_model = form.pay_model;
      update.pay_rate = rate;
    } else if (staff.pay_model || staff.pay_rate !== null && staff.pay_rate !== undefined) {
      // Admin explicitly cleared both — persist the clear.
      update.pay_model = null;
      update.pay_rate = null;
    }

    setLoading(true);
    try {
      const response = await axios.put(`${API}/auth/staff/${staff.user_id}`, update);
      onSave(response.data);
      toast.success("Staff updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update staff");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle>Edit Staff: {staff.email}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label className="text-slate-400">Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-slate-800 border-slate-700"
            />
          </div>
          <div>
            <Label className="text-slate-400">New Password (leave blank to keep current)</Label>
            <Input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="bg-slate-800 border-slate-700"
              placeholder="Min 6 characters"
            />
          </div>
          <PayConfigFields
            payModel={form.pay_model}
            payRate={form.pay_rate}
            onChange={(patch) => setForm({ ...form, ...patch })}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-700">Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-saturn-orange">
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Security Tab (Admin Only)
const SecurityTab = () => {
  const [securityStatus, setSecurityStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSecurityStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/security/status`);
      setSecurityStatus(response.data);
    } catch (error) {
      console.error("Failed to load security status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSecurityStatus();
    // Refresh every 30 seconds
    const interval = setInterval(fetchSecurityStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleUnlockAccount = async (email) => {
    try {
      await axios.post(`${API}/auth/security/unlock-account?email=${encodeURIComponent(email)}`, {});
      toast.success(`Account ${email} unlocked`);
      fetchSecurityStatus();
    } catch (error) {
      toast.error("Failed to unlock account");
    }
  };

  const handleUnbanIP = async (ip) => {
    try {
      await axios.post(`${API}/auth/security/unban-ip?ip=${encodeURIComponent(ip)}`, {});
      toast.success(`IP ${ip} unbanned`);
      fetchSecurityStatus();
    } catch (error) {
      toast.error("Failed to unban IP");
    }
  };

  if (loading) {
    return <div className="text-center text-slate-400 py-8">Loading security status...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Shield className="w-5 h-5 text-saturn-orange" />
          Security Dashboard
        </h2>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchSecurityStatus}
          className="border-slate-700 text-slate-300"
        >
          Refresh
        </Button>
      </div>

      {/* Security Config */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-sm">Protection Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
            <div className="text-center p-3 bg-slate-900/50 rounded-lg">
              <div className="text-saturn-orange font-bold">{securityStatus?.config?.max_attempts_per_ip}</div>
              <div className="text-slate-400 text-xs">Max attempts/IP</div>
            </div>
            <div className="text-center p-3 bg-slate-900/50 rounded-lg">
              <div className="text-saturn-orange font-bold">{securityStatus?.config?.max_attempts_per_account}</div>
              <div className="text-slate-400 text-xs">Max attempts/account</div>
            </div>
            <div className="text-center p-3 bg-slate-900/50 rounded-lg">
              <div className="text-saturn-orange font-bold">{Math.round(securityStatus?.config?.ip_window_seconds / 60)}m</div>
              <div className="text-slate-400 text-xs">Time window</div>
            </div>
            <div className="text-center p-3 bg-slate-900/50 rounded-lg">
              <div className="text-saturn-orange font-bold">{Math.round(securityStatus?.config?.account_lockout_seconds / 60)}m</div>
              <div className="text-slate-400 text-xs">Account lockout</div>
            </div>
            <div className="text-center p-3 bg-slate-900/50 rounded-lg">
              <div className="text-saturn-orange font-bold">{Math.round(securityStatus?.config?.ip_ban_seconds / 60)}m</div>
              <div className="text-slate-400 text-xs">IP ban duration</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Locked Accounts */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-500" />
            Locked Accounts ({securityStatus?.locked_accounts?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {securityStatus?.locked_accounts?.length > 0 ? (
            <div className="space-y-2">
              {securityStatus.locked_accounts.map((account, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                  <div>
                    <div className="text-white font-medium">{account.email}</div>
                    <div className="text-slate-400 text-xs">
                      Unlocks in {Math.ceil(account.unlocks_in_seconds / 60)} minutes
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => handleUnlockAccount(account.email)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Unlock
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-400 text-center py-4">No accounts currently locked</p>
          )}
        </CardContent>
      </Card>

      {/* Banned IPs */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Shield className="w-4 h-4 text-red-500" />
            Banned IPs ({securityStatus?.banned_ips?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {securityStatus?.banned_ips?.length > 0 ? (
            <div className="space-y-2">
              {securityStatus.banned_ips.map((ban, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                  <div>
                    <div className="text-white font-medium font-mono">{ban.ip}</div>
                    <div className="text-slate-400 text-xs">
                      Unbans in {Math.ceil(ban.unbans_in_seconds / 60)} minutes
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleUnbanIP(ban.ip)}
                    className="border-red-500 text-red-400 hover:bg-red-500/20"
                  >
                    Unban
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-400 text-center py-4">No IPs currently banned</p>
          )}
        </CardContent>
      </Card>

      {/* Security Info */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <p className="text-slate-400 text-sm">
            <strong className="text-white">How it works:</strong> After {securityStatus?.config?.max_attempts_per_account} failed login attempts, 
            an account is locked for {Math.round(securityStatus?.config?.account_lockout_seconds / 60)} minutes. 
            If an IP makes {securityStatus?.config?.max_attempts_per_ip * 2}+ attempts, it gets banned for {Math.round(securityStatus?.config?.ip_ban_seconds / 60)} minutes.
            Successful logins clear all failed attempt history.
          </p>
        </CardContent>
      </Card>

      {/* Audit Log */}
      <AuditLogCard />
    </div>
  );
};

// Quote Requests Tab
const QuotesTab = ({ isAdmin }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes/`);
      setQuotes(response.data);
    } catch (error) {
      console.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-white">Quote Requests</h2>
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700 hover:bg-transparent">
                <TableHead className="text-slate-400">Date</TableHead>
                <TableHead className="text-slate-400">Customer</TableHead>
                <TableHead className="text-slate-400">Service</TableHead>
                <TableHead className="text-slate-400">Vehicle</TableHead>
                <TableHead className="text-slate-400">Contact</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-slate-400 py-8">Loading...</TableCell>
                </TableRow>
              ) : quotes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-slate-400 py-8">No quote requests</TableCell>
                </TableRow>
              ) : (
                quotes.map((quote, idx) => (
                  <TableRow key={idx} className="border-slate-700">
                    <TableCell className="text-slate-400">
                      {quote.created_at ? new Date(quote.created_at).toLocaleDateString() : "N/A"}
                    </TableCell>
                    <TableCell className="text-white">{quote.customer_name}</TableCell>
                    <TableCell className="text-slate-300">{quote.service_name}</TableCell>
                    <TableCell className="text-slate-300">
                      {quote.vehicle_year} {quote.vehicle_make} {quote.vehicle_model}
                    </TableCell>
                    <TableCell>
                      <div className="text-slate-300 text-sm">{quote.customer_email}</div>
                      <div className="text-slate-400 text-sm">{quote.customer_phone}</div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Dashboard Component
const DashboardPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Job modals state (shared for job board)
  const [completingBooking, setCompletingBooking] = useState(null);
  const [viewingJobCard, setViewingJobCard] = useState(null);
  const [checkingInBooking, setCheckingInBooking] = useState(null);
  const [jobBoardRefresh, setJobBoardRefresh] = useState(0);

  const isAdmin = user?.role === "admin";
  // Per-staff permission flags, read fresh by the backend on every
  // request. /auth/me returns the caller's own flags so the UI can
  // show the Payment panel and the Take-cash button.
  const permissions = user?.permissions || {};
  const isStaff = user?.role === "staff";
  const hasAccess = isAdmin || isStaff;

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/");
    }
    if (!authLoading && user && !hasAccess) {
      toast.error("Access denied. Partner or staff account required.");
      navigate("/");
    }
  }, [user, authLoading, navigate, hasAccess]);

  useEffect(() => {
    if (hasAccess) {
      fetchData();
    }
  }, [hasAccess]);

  const fetchData = async () => {
    try {
      const [bookingsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/auth/bookings/all`),
        axios.get(`${API}/services/`)
      ]);
      setBookings(bookingsRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      console.error("Failed to fetch data", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Session expired. Please log in again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-saturn-black flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  return (
    <div className="min-h-screen bg-saturn-black flex overflow-x-hidden">
      <Sidebar
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isAdmin={isAdmin}
      />

      <main className="flex-1 min-w-0 max-w-full p-6 lg:p-8 overflow-x-hidden overflow-y-auto">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            className="text-white"
          >
            <Menu className="w-6 h-6" />
          </Button>
          <h1 className="font-orbitron font-bold text-white">Dashboard</h1>
          <div className="w-10" />
        </div>

        {/* Page Header */}
        <div className="mb-8 flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-white font-orbitron capitalize">{activeTab}</h1>
            <p className="text-slate-400">
              {isAdmin ? "Full admin access" : "Staff view (read-only)"}
            </p>
          </div>
          {isAdmin && (
            <div className="flex-shrink-0 flex items-center gap-2 flex-wrap" data-testid="dashboard-header-widgets">
              <NewReviewsAlertWidget />
              <CollectionEmailAuditWidget />
              <ReviewFunnelWidget />
            </div>
          )}
        </div>

        {/* Content */}
        {activeTab === "overview" && <OverviewTab bookings={bookings} setBookings={setBookings} isAdmin={isAdmin} />}
        {activeTab === "jobboard" && (
          <JobBoardSection
            isAdmin={isAdmin}
            currentUserId={user?.user_id}
            onCheckIn={(booking) => setCheckingInBooking(booking)}
            onComplete={(booking) => setCompletingBooking(booking)}
            onViewCard={(booking) => setViewingJobCard(booking)}
            refreshTrigger={jobBoardRefresh}
          />
        )}
        {activeTab === "bookings" && (
          <BookingsTab permissions={permissions}
            bookings={bookings}
            setBookings={setBookings}
            isAdmin={isAdmin}
            services={services}
          />
        )}
        {activeTab === "quotes" && isAdmin && <QuotesTab isAdmin={isAdmin} />}
        {activeTab === "stock" && (
          <div className="space-y-6">
            <StockPullSection />
          </div>
        )}
        {activeTab === "inventory" && isAdmin && (
          <div className="space-y-4">
            <StockValuationCard compact />
            <StockInventorySection isAdmin={isAdmin} />
          </div>
        )}
        {activeTab === "finance" && isAdmin && <FinanceSection />}
        {activeTab === "promotions" && isAdmin && <PromotionsTab />}
        {activeTab === "stocktake" && <StockTakeSection isAdmin={isAdmin} />}
        {activeTab === "invoices" && isAdmin && <InvoicesTab />}
        {activeTab === "blockcal" && isAdmin && <BlockCalendarSection />}
        {activeTab === "blog" && isAdmin && <BlogAdminTab />}
        {activeTab === "analytics" && isAdmin && <StockAnalytics />}
        {activeTab === "profitability" && isAdmin && <JobProfitability />}
        {activeTab === "job-alerts" && isAdmin && <JobAlertsSection />}
        {activeTab === "staff" && isAdmin && <StaffTab />}
        {activeTab === "security" && isAdmin && <SecurityTab />}
        {activeTab === "settings" && (
          <div className="space-y-6">
            {isAdmin && (
              <Card className="bg-slate-800/50 border-slate-700" data-testid="data-setup-card">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Package className="w-5 h-5 text-saturn-orange" />
                    Data Setup
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-slate-400 text-sm">
                    Load the default product catalogue (tint films, wraps, PPF, chemicals, tools — 46 items).
                    Safe to run anytime: existing products are never duplicated or modified. After seeding,
                    add your physical rolls/packs in the Inventory tab.
                  </p>
                  <Button
                    data-testid="seed-stock-btn"
                    className="bg-saturn-orange hover:bg-saturn-orange/80"
                    onClick={async (e) => {
                      const btn = e.currentTarget;
                      btn.disabled = true;
                      try {
                        const res = await axios.post(`${API}/stock/seed-defaults`);
                        toast.success(res.data.message);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Seeding failed");
                      } finally {
                        btn.disabled = false;
                      }
                    }}
                  >
                    Seed Default Stock Products
                  </Button>
                </CardContent>
              </Card>
            )}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-8 text-center text-slate-400">
                More settings coming soon...
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Job Board Modals */}
      <JobCompletionModal
        isOpen={!!completingBooking}
        onClose={() => setCompletingBooking(null)}
        booking={completingBooking}
        onComplete={() => {
          setCompletingBooking(null);
          setJobBoardRefresh(prev => prev + 1);
          fetchData();
        }}
      />
      <JobCardViewModal
        isAdmin={isAdmin}
        permissions={permissions}
        isOpen={!!viewingJobCard}
        onClose={() => setViewingJobCard(null)}
        booking={viewingJobCard}
      />
      <JobCheckInModal
        isAdmin={isAdmin}
        isOpen={!!checkingInBooking}
        onClose={() => setCheckingInBooking(null)}
        booking={checkingInBooking}
        onCheckInComplete={(data) => {
          setBookings(prev => prev.map(b => 
            b.booking_id === checkingInBooking?.booking_id 
              ? { ...b, status: "checked_in", vehicle_reg: data.vehicle_reg }
              : b
          ));
          setCheckingInBooking(null);
          setJobBoardRefresh(prev => prev + 1);
        }}
      />
    </div>
  );
};

export default DashboardPage;
